'use client';

import {useState, useCallback} from 'react';
import {useLocale, useTranslations} from 'next-intl';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';

const API = process.env.NEXT_PUBLIC_API_URL || '/api/v1';

const CATEGORIES = [
  {value: 'education', label_fr: 'Education', label_ar: 'التعليم', label_en: 'Education'},
  {value: 'sante', label_fr: 'Sante', label_ar: 'الصحة', label_en: 'Health'},
  {value: 'logement', label_fr: 'Logement', label_ar: 'السكن', label_en: 'Housing'},
  {value: 'microcredit', label_fr: 'Microcredit', label_ar: 'القروض الصغرى', label_en: 'Microcredit'},
  {value: 'infrastructure', label_fr: 'Infrastructure', label_ar: 'البنية التحتية', label_en: 'Infrastructure'},
  {value: 'environnement', label_fr: 'Environnement', label_ar: 'البيئة', label_en: 'Environment'},
  {value: 'culture', label_fr: 'Culture', label_ar: 'الثقافة', label_en: 'Culture'},
];

interface Jalon {
  titre: string;
  date_prevue: string;
}

interface GeoOption {
  id: number;
  nom: string;
}

export default function NouveauProjetPage() {
  const locale = useLocale();
  const t = useTranslations('workflow');

  // Form state
  const [titreFr, setTitreFr] = useState('');
  const [titreAr, setTitreAr] = useState('');
  const [description, setDescription] = useState('');
  const [categorie, setCategorie] = useState('');
  const [budgetObjectif, setBudgetObjectif] = useState('');
  const [urgent, setUrgent] = useState(false);

  // Geo cascade
  const [gouvernorats, setGouvernorats] = useState<GeoOption[]>([]);
  const [delegations, setDelegations] = useState<GeoOption[]>([]);
  const [imadas, setImadas] = useState<GeoOption[]>([]);
  const [gouvernoratId, setGouvernoratId] = useState('');
  const [delegationId, setDelegationId] = useState('');
  const [imadaId, setImadaId] = useState('');

  // Photos
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  // Jalons
  const [jalons, setJalons] = useState<Jalon[]>([
    {titre: '', date_prevue: ''},
    {titre: '', date_prevue: ''},
    {titre: '', date_prevue: ''},
  ]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch gouvernorats on mount
  const fetchGouvernorats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/geo/gouvernorats`);
      if (res.ok) setGouvernorats(await res.json());
    } catch { /* API not available in dev */ }
  }, []);

  // Lazy load on first render
  useState(() => { fetchGouvernorats(); });

  const handleGouvernoratChange = async (id: string) => {
    setGouvernoratId(id);
    setDelegationId('');
    setImadaId('');
    setDelegations([]);
    setImadas([]);
    if (id) {
      try {
        const res = await fetch(`${API}/geo/gouvernorats/${id}/delegations`);
        if (res.ok) setDelegations(await res.json());
      } catch { /* */ }
    }
  };

  const handleDelegationChange = async (id: string) => {
    setDelegationId(id);
    setImadaId('');
    setImadas([]);
    if (id) {
      try {
        const res = await fetch(`${API}/geo/delegations/${id}/imadas`);
        if (res.ok) setImadas(await res.json());
      } catch { /* */ }
    }
  };

  // Photos upload
  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = [...photos, ...files].slice(0, 10);
    setPhotos(newPhotos);
    const previews = newPhotos.map((f) => URL.createObjectURL(f));
    setPhotoPreviews(previews);
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    URL.revokeObjectURL(photoPreviews[index]);
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== index));
  };

  // Jalons
  const addJalon = () => {
    setJalons([...jalons, {titre: '', date_prevue: ''}]);
  };

  const updateJalon = (index: number, field: keyof Jalon, value: string) => {
    const updated = [...jalons];
    updated[index] = {...updated[index], [field]: value};
    setJalons(updated);
  };

  const removeJalon = (index: number) => {
    if (jalons.length <= 3) return; // min 3
    setJalons(jalons.filter((_, i) => i !== index));
  };

  // Validation
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!titreFr.trim()) errs.titreFr = 'Le titre en francais est requis';
    if (!titreAr.trim()) errs.titreAr = 'Le titre en arabe est requis';
    if (!description.trim()) errs.description = 'La description est requise';
    if (!categorie) errs.categorie = 'La categorie est requise';
    if (!budgetObjectif || Number(budgetObjectif) <= 0) errs.budgetObjectif = 'Le budget objectif est requis';
    if (photos.length < 2) errs.photos = 'Minimum 2 photos requises';
    const validJalons = jalons.filter((j) => j.titre.trim() && j.date_prevue);
    if (validJalons.length < 3) errs.jalons = 'Minimum 3 jalons avec titre et date requis';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit
  const handleSubmit = async (brouillon: boolean) => {
    if (!brouillon && !validate()) return;
    setLoading(true);

    const body: Record<string, unknown> = {
      titre_fr: titreFr,
      titre_ar: titreAr || undefined,
      description_fr: description,
      categorie,
      budget_objectif: Number(budgetObjectif),
      urgent,
      gouvernorat_id: gouvernoratId ? Number(gouvernoratId) : undefined,
      delegation_id: delegationId ? Number(delegationId) : undefined,
    };

    try {
      const token = localStorage.getItem('utss_token');
      const res = await fetch(`${API}/projets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? {Authorization: `Bearer ${token}`} : {}),
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = `/${locale}/projets/${data.id}`;
      } else {
        const err = await res.json().catch(() => null);
        setErrors({submit: err?.detail || 'Erreur lors de la creation du projet'});
      }
    } catch {
      setErrors({submit: 'Erreur de connexion au serveur'});
    } finally {
      setLoading(false);
    }
  };

  const labelFor = (cat: typeof CATEGORIES[0]) => {
    if (locale === 'ar') return cat.label_ar;
    if (locale === 'en') return cat.label_en;
    return cat.label_fr;
  };

  const inputClass = (field: string) =>
    `w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors ${
      errors[field] ? 'border-red-500 error-field' : 'border-border'
    }`;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="text-sm text-muted mb-6">
        <a href={`/${locale}`} className="hover:text-primary">Accueil</a>
        <span className="mx-2">/</span>
        <a href={`/${locale}/projets`} className="hover:text-primary">Projets</a>
        <span className="mx-2">/</span>
        <span className="text-foreground">{t('create_project')}</span>
      </nav>

      <h1 className="text-3xl font-bold text-primary mb-8">{t('create_project')}</h1>

      {errors.submit && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {errors.submit}
        </div>
      )}

      <div className="space-y-8">
        {/* Informations generales */}
        <Card>
          <h2 className="text-xl font-semibold text-primary mb-6">Informations générales</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titre (Francais) *</label>
              <input
                type="text"
                value={titreFr}
                onChange={(e) => setTitreFr(e.target.value)}
                className={inputClass('titreFr')}
                placeholder="Titre du projet en francais"
              />
              {errors.titreFr && <p className="text-red-500 text-xs mt-1">{errors.titreFr}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Titre (Arabe) *</label>
              <input
                type="text"
                value={titreAr}
                onChange={(e) => setTitreAr(e.target.value)}
                className={inputClass('titreAr')}
                dir="rtl"
                placeholder="عنوان المشروع بالعربية"
              />
              {errors.titreAr && <p className="text-red-500 text-xs mt-1">{errors.titreAr}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={`${inputClass('description')} min-h-[120px]`}
                rows={5}
                placeholder="Décrivez le projet, ses objectifs, les bénéficiaires..."
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Catégorie *</label>
                <select
                  value={categorie}
                  onChange={(e) => setCategorie(e.target.value)}
                  className={inputClass('categorie')}
                >
                  <option value="">-- Choisir --</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{labelFor(cat)}</option>
                  ))}
                </select>
                {errors.categorie && <p className="text-red-500 text-xs mt-1">{errors.categorie}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('budget')} (TND) *</label>
                <input
                  type="number"
                  value={budgetObjectif}
                  onChange={(e) => setBudgetObjectif(e.target.value)}
                  className={inputClass('budgetObjectif')}
                  min="1"
                  placeholder="Ex: 50000"
                />
                {errors.budgetObjectif && <p className="text-red-500 text-xs mt-1">{errors.budgetObjectif}</p>}
              </div>
            </div>

            {/* Toggle urgent */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setUrgent(!urgent)}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                  urgent ? 'bg-secondary' : 'bg-muted/30'
                }`}
                role="switch"
                aria-checked={urgent}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    urgent ? 'translate-x-6' : ''
                  }`}
                />
              </button>
              <span className="text-sm font-medium">Projet urgent</span>
              {urgent && <Badge variant="accent">Urgent</Badge>}
            </div>
          </div>
        </Card>

        {/* Localisation */}
        <Card>
          <h2 className="text-xl font-semibold text-primary mb-6">Localisation</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Gouvernorat</label>
              <select
                value={gouvernoratId}
                onChange={(e) => handleGouvernoratChange(e.target.value)}
                className={inputClass('')}
              >
                <option value="">-- Gouvernorat --</option>
                {gouvernorats.map((g) => (
                  <option key={g.id} value={g.id}>{g.nom}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Délégation</label>
              <select
                value={delegationId}
                onChange={(e) => handleDelegationChange(e.target.value)}
                className={inputClass('')}
                disabled={!gouvernoratId}
              >
                <option value="">-- Délégation --</option>
                {delegations.map((d) => (
                  <option key={d.id} value={d.id}>{d.nom}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Imada</label>
              <select
                value={imadaId}
                onChange={(e) => setImadaId(e.target.value)}
                className={inputClass('')}
                disabled={!delegationId}
              >
                <option value="">-- Imada --</option>
                {imadas.map((im) => (
                  <option key={im.id} value={im.id}>{im.nom}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Photos */}
        <Card>
          <h2 className="text-xl font-semibold text-primary mb-2">{t('photos')}</h2>
          <p className="text-sm text-muted mb-4">Minimum 2 photos, maximum 10</p>
          {errors.photos && <p className="text-red-500 text-xs mb-3">{errors.photos}</p>}

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
            {photoPreviews.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border group">
                <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  X
                </button>
              </div>
            ))}

            {photos.length < 10 && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary flex items-center justify-center cursor-pointer transition-colors">
                <div className="text-center text-muted">
                  <svg className="w-8 h-8 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-xs">Ajouter</span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoAdd}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </Card>

        {/* Jalons */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-primary">{t('milestones')}</h2>
              <p className="text-sm text-muted">Minimum 3 jalons requis</p>
            </div>
            <Button variant="outline" size="sm" onClick={addJalon}>
              + Ajouter un jalon
            </Button>
          </div>
          {errors.jalons && <p className="text-red-500 text-xs mb-3">{errors.jalons}</p>}

          <div className="space-y-3">
            {jalons.map((jalon, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                  {i + 1}
                </span>
                <input
                  type="text"
                  value={jalon.titre}
                  onChange={(e) => updateJalon(i, 'titre', e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-border text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  placeholder="Titre du jalon"
                />
                <input
                  type="date"
                  value={jalon.date_prevue}
                  onChange={(e) => updateJalon(i, 'date_prevue', e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
                {jalons.length > 3 && (
                  <button
                    type="button"
                    onClick={() => removeJalon(i)}
                    className="w-8 h-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center cursor-pointer"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            size="lg"
            onClick={() => handleSubmit(true)}
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer brouillon'}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={() => handleSubmit(false)}
            disabled={loading}
          >
            {loading ? 'Soumission...' : t('submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
